import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { semillaPorCategoria, type ProductoSemilla } from '@abastece/shared';
import { db } from '../db/dexie.js';
import { crearProducto } from '../lib/catalogo.js';
import { normalizar } from '../lib/texto.js';
import { usePuede } from '../lib/sesion.js';
import { irA } from '../lib/volver.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { Ico } from '../ui/Iconos.js';
import { useT } from '../i18n/index.js';
import { etiquetaCategoriaProducto } from '../i18n/dominio.js';

export function Semilla() {
  const t = useT();
  const puede = usePuede('editar_productos');
  /** Pricing them is the owner's (2026-10-13): a cashier who loads the catalogue is told so. */
  const puedePrecios = usePuede('editar_precios');
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);

  const porCategoria = useMemo(() => semillaPorCategoria(), []);
  const [elegidas, setElegidas] = useState<Set<string>>(() => new Set(porCategoria.keys()));
  const [cargando, setCargando] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [listo, setListo] = useState<number | null>(null);

  /** Matched by normalised name, so re-running this never duplicates what is already there. */
  const existentes = useMemo(
    () => new Set(productos.map((p) => normalizar(p.nombre))),
    [productos],
  );

  const faltantes = (categoria: string): ProductoSemilla[] =>
    (porCategoria.get(categoria) ?? []).filter((p) => !existentes.has(normalizar(p.nombre)));

  const aCargar = useMemo(
    () => [...elegidas].flatMap((c) => faltantes(c)),
    [elegidas, existentes, porCategoria],
  );

  async function cargar() {
    setCargando(true);
    setProgreso(0);
    try {
      let n = 0;
      for (const p of aCargar) {
        await crearProducto({
          nombre: p.nombre,
          nombreEn: p.nombreEn,
          categoria: p.categoria,
          unidadBase: p.unidadBase,
          ...(p.esMedicamento ? { esMedicamento: true } : {}),
          ...(p.perecedero ? { perecedero: true } : {}),
        });
        n += 1;
        setProgreso(n);
      }
      setListo(n);
    } finally {
      setCargando(false);
    }
  }

  if (!puede) {
    return (
      <Pagina titulo={t('semilla.titulo')}>
        <Vacio mensaje={t('semilla.sinPermiso')} />
      </Pagina>
    );
  }

  if (listo !== null) {
    return (
      <Pagina titulo={t('semilla.titulo')} volverA="/productos">
        <Vacio mensaje={t('semilla.agregados', { n: listo })}>
          <p className="max-w-sm text-xs text-slate-500">{t(puedePrecios ? 'semilla.sinPreciosAun' : 'semilla.sinPreciosDelDueno')}</p>
          {/* The next thing is pricing them, not admiring them. Hand straight over. */}
          {puedePrecios && (
            <button
              type="button"
              className="btn-primario px-5"
              onClick={() => void irA({ to: '/precios' })}
            >
              <Ico icono="precios" />
              {t('arranque.precios.accion')}
            </button>
          )}
          <button
            type="button"
            className="btn-texto gap-1.5 text-sm text-slate-400"
            onClick={() => void irA({ to: '/productos' })}
          >
            <Ico icono="productos" className="size-4" />
            {t('semilla.verCatalogo')}
          </button>
        </Vacio>
      </Pagina>
    );
  }

  return (
    <Pagina
      titulo={t('semilla.titulo')}
      subtitulo={t('semilla.porAgregar', { n: aCargar.length })}
      volverA="/productos"
    >
      <p className="mb-4 text-sm text-slate-400">
        {t('semilla.explicacion')}
      </p>

      <ul className="mb-4 flex flex-col gap-2">
        {[...porCategoria.keys()].map((categoria) => {
          const pendientes = faltantes(categoria);
          const total = porCategoria.get(categoria)!.length;
          const marcada = elegidas.has(categoria);
          return (
            <li key={categoria}>
              <label
                className={`tarjeta flex cursor-pointer items-center gap-3 ${
                  pendientes.length === 0 ? 'opacity-50' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={marcada && pendientes.length > 0}
                  disabled={pendientes.length === 0}
                  onChange={(e) =>
                    setElegidas((s) => {
                      const next = new Set(s);
                      if (e.target.checked) next.add(categoria);
                      else next.delete(categoria);
                      return next;
                    })
                  }
                  className="size-5 shrink-0 accent-teal-500"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">
                    {etiquetaCategoriaProducto(categoria)}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {pendientes.length === 0
                      ? t('semilla.yaEstan', { total })
                      : t('semilla.pendientes', { n: pendientes.length, total })}
                  </span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      {cargando && (
        <p className="mb-3 text-sm text-slate-400">
          {t('semilla.agregando', { n: progreso, total: aCargar.length })}
        </p>
      )}

      <button
        type="button"
        className="btn-primario w-full"
        disabled={cargando || aCargar.length === 0}
        onClick={() => void cargar()}
      >
        <Ico icono="semilla" />
        {aCargar.length === 0
          ? t('semilla.todoCargado')
          : t('semilla.agregar', { n: aCargar.length })}
      </button>

      <p className="mt-4 text-xs text-slate-500">
        {t('semilla.notaFinal')}
      </p>
    </Pagina>
  );
}
